import NewForm from "components/NewForm";
import type { TAddressAutococompleteOption } from "components/shared/AddressAutocomplete";
import { getAddressEditOption } from "components/shared/AddressAutocomplete";
import DeleteModal from "components/shared/DeleteModal";
import type { IFormStep } from "containers/TermFormContainer";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferee } from "store/transfereesStore";
import { createTransferee, deleteTransferee, updateTransferee } from "store/transfereesStore";

import AddressStep from "./AddressStep";
import NewTransfereeStep from "./NewTransfereeStep";

interface ITransfereesFormContainer {
    mode: "new" | "edit-page";
    /**
     * Is edit mode active
     */
    editMode?: boolean;
    /**
     * Closes edit mode.
     */
    closeEditMode?: () => void;
    /**
     * Data of the transferee (for edit).
     */
    transferee?: ITransferee;
    /**
     * To know if form is called from other form (like from TransferTerm form).
     */
    from?: string;
    /**
     * When coming from other form, triggers closing page and updating data.
     */
    handleClose?: (transferee: ITransferee) => void;
    /**
     * Needed to close Transferee form when coming from TransferTerm form.
     */
    setAddNewTransferee?: Dispatch<SetStateAction<boolean | undefined>>;
}

export interface ITransfereeData {
    name: string;
    avatarUrl: string;
    city: string;
    country: { name: string; flag: string };
    email: string;
    intraCommunityVat: string;
    siretNumber: string;
    street: string;
    zipCode: string;
}

export interface ITransfereeFormValues {
    name: string | undefined;
    avatarUrl: string | undefined;
    email: string | undefined;
    city: string | undefined;
    country: { name: string; flag: string };
    zipCode: string | undefined;
    street: string | undefined | TAddressAutococompleteOption;
    siretNumber: string | undefined;
    intraCommunityVat: string | undefined;
}

export interface ITransfereeFormData {
    control: Control<ITransfereeFormValues>;
    errors: FieldErrors<ITransfereeFormValues>;
    watch: UseFormWatch<ITransfereeFormValues>;
    setValue: UseFormSetValue<ITransfereeFormValues>;
}

/**
 * Form to create a transferee.
 */
const TransfereesFormContainer = ({
    mode,
    editMode,
    closeEditMode,
    transferee,
    from,
    handleClose,
    setAddNewTransferee,
}: ITransfereesFormContainer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { createTransfereeStatus, transferee: newTransferee } = useAppSelector((state) => state.transferees);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const defaultValues: ITransfereeFormValues = useMemo(() => {
        return {
            name: editMode ? transferee?.name ?? "" : "",
            avatarUrl: editMode ? transferee?.avatarUrl : "",
            email: editMode ? transferee?.email ?? "" : "",
            city: editMode ? transferee?.city ?? "" : "",
            country: { name: editMode ? transferee?.country ?? "" : "", flag: "" },
            zipCode: editMode ? transferee?.zipCode ?? "" : "",
            street: editMode ? getAddressEditOption(transferee) ?? "" : "",
            siretNumber: editMode ? transferee?.siretNumber ?? "" : "",
            intraCommunityVat: editMode ? transferee?.intraCommunityVat ?? "" : "",
        };
    }, [transferee, editMode]);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        trigger,
        formState: { errors, isDirty, dirtyFields },
    } = useForm<ITransfereeFormValues>({
        defaultValues,
        mode: "all",
    });

    const chosenName = watch("name");

    const onSubmit = async (data: ITransfereeData) => {
        // Because the field of street is an autocomplete,
        // if they selected an option, the value is an object.
        if (typeof data.street !== "string") {
            data.street = (data.street as TAddressAutococompleteOption).street;
        }

        const req = {
            name: data.name ?? "",
            email: data.email ?? "",
            avatarUrl: data.avatarUrl ?? "",
            city: data.city ?? "",
            country: data.country.name ?? "",
            zipCode: data.zipCode ?? "",
            street: data.street ?? "",
            siretNumber: data.siretNumber ?? "",
            intraCommunityVat: data.intraCommunityVat ?? "",
        };
        if (mode === "new") {
            const response = await dispatch(createTransferee({ data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                if (from === "otherForm") {
                    handleClose && handleClose(response.payload);
                    return;
                }
            }
        } else if (transferee) {
            const response = await dispatch(updateTransferee({ transfereeId: transferee.id, data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                closeEditMode && closeEditMode();
            }
        }
    };

    useEffect(() => {
        if (from === "otherForm") return;
        if (createTransfereeStatus === "succeeded") {
            navigate(`/transferees/${newTransferee.id}`);
        }
    }, [createTransfereeStatus]);

    const handleDeleteTransferee = async () => {
        if (transferee) {
            const response = await dispatch(deleteTransferee({ transfereeId: transferee.id }));
            if (response.meta.requestStatus === "fulfilled") {
                closeEditMode && closeEditMode();
            }
        }
    };

    const steps: IFormStep[] = [
        {
            stepTitle: t("Transferees.TheTransferee"),
            stepSubtitle: t("Transferees.TheTransfereeSubtitle"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <NewTransfereeStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    mode={mode}
                    indexStep={0}
                    useFormData={{ control, errors, watch, setValue }}
                />
            ),
        },
        {
            stepTitle: t("Beneficiars.Address"),
            stepSubtitle: t("Transferees.AddressSubtitle"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <AddressStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    useFormData={{ control, errors, watch, setValue }}
                    indexStep={1}
                />
            ),
        },
    ];

    const deleteStep = {
        title: t("Transferees.DeleteTransferee"),
        subtitle: t("Transferees.DeleteSubtitle"),
    };

    const deleteDescription = (
        <>
            <p>{t("Transferees.DeleteText2")}</p>
            <p>{t("Transferees.DeleteText3")}</p>
        </>
    );

    return (
        <div className="transferee-form-container">
            <NewForm
                steps={steps}
                dataForSteps={[`${chosenName}`]}
                useFormData={{
                    trigger,
                    handleSubmit,
                    isDirty,
                    dirtyFields,
                }}
                onSubmit={onSubmit}
                mode={mode}
                editModeActive={editMode}
                closeEditMode={closeEditMode}
                deleteStep={deleteStep}
                onDelete={() => setShowDeleteModal(true)}
                txtBtnSubmitNew={t("Transferees.CreateTransferee")}
                handleCloseButton={() =>
                    from === "otherForm" && setAddNewTransferee ? setAddNewTransferee(false) : navigate(-1)
                }
                infoLink={{
                    type: t("TransferTerms.TermTable.Transferee").toLowerCase(),
                    link: "https://helpdesk.crealo.app/fr/article/creer-un-cessionnaire-1n3kfl8//",
                }}
            />
            <DeleteModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onDelete={handleDeleteTransferee}
                elementTitle={t("DeleteModal.TheTransferee", { name: transferee && transferee?.name })}
                needsConfirmation={!!(transferee?.transferTermCount && transferee?.transferTermCount > 0)}
                confirmationText={t("Transferees.DeleteTransfereeCaps")}
                description={
                    transferee?.transferTermCount && transferee?.transferTermCount > 0
                        ? deleteDescription
                        : t("Transferees.DeleteText1")
                }
            />
        </div>
    );
};

export default TransfereesFormContainer;
